import * as React from 'react';
import { Overlay } from '../components/Overlay';
import { ThumbsGrid } from '../components/ThumbsGrid';
import Layer from '../data/Layer';
import { getStoreState, storeDispatch } from '../state/Store';
import { MeshSpectrum, SoundWaveType } from '@beatflyer/shaders-lib';
import { Soundtrack } from 'audio/Soundtrack';
import { trackEvent } from 'utils/Tracker';
import { AddSoundWaveFrameCommand } from 'state/commands/AddSoundWaveFrameCommand';

const PIXI = (window as any).PIXI;

const types = [
  SoundWaveType.FREQ_LINE,
  SoundWaveType.FILL,
  SoundWaveType.BARS,
  SoundWaveType.BARS_CIRCLE,
  SoundWaveType.CIRCLE,
  SoundWaveType.LINE,
  SoundWaveType.SEMIFILL,
  SoundWaveType.FILL_BOTTOM,
  SoundWaveType.CONCENTRIC_CIRCLES,
  SoundWaveType.CONCENTRIC_RECTANGLES
];

interface OptionProps {
  type: SoundWaveType;
  didSelect: (type: SoundWaveType) => void;
}
interface OptionState {
  isOver: boolean;
}
class SoundwaveOption extends React.Component<OptionProps, OptionState> {
  state: OptionState = { isOver: false };
  canvasEl = React.createRef<HTMLCanvasElement>();
  renderer: PIXI.WebGLRenderer;
  stage: PIXI.Container;
  mesh: MeshSpectrum;
  animationFrameReq = -1;

  get soundtrack(): Soundtrack {
    return getStoreState().flyer.soundtrack!;
  }

  didEnter = () => {
    this.setState({ isOver: true });
  };

  didLeave = () => {
    this.setState({ isOver: false });
  };

  componentWillUnmount() {
    this.disposeMesh();
  }

  componentDidUpdate(prevProps: OptionProps, prevState: OptionState) {
    if (prevState.isOver === this.state.isOver) {
      // do nothing
      return;
    }
    if (this.state.isOver) {
      const canvas = this.canvasEl.current;
      // set up pixi
      if (canvas) {
        let { width, height } = canvas.getBoundingClientRect();
        width *= 2;
        height *= 2;
        canvas.width = width;
        canvas.height = height;
        this.renderer = new PIXI.Renderer({
          width,
          height,
          transparent: true,
          view: canvas
        });
        (this.renderer as any).context.gl.getExtension(
          'OES_standard_derivatives'
        );
        this.stage = new PIXI.Container();
        const meshId = 'mesh-sound-wave-' + this.props.type;
        const clazz = (window as any).BeatFlyer.filters[meshId];
        if (!clazz) {
          throw new Error(`Cannot find mesh ${meshId}`);
        }
        this.mesh = new clazz({
          props: {
            color: '#1a1a1a'
          }
        });
        this.mesh.x = width / 2;
        this.mesh.y = height / 2;
        this.mesh.width = width;
        this.mesh.height = height;
        this.stage.addChild(this.mesh);
        this.soundtrack.resume();
        this.renderMesh();
      }
    } else {
      this.disposeMesh();
    }
  }

  disposeMesh = () => {
    this.soundtrack.pause();
    if (this.renderer) {
      this.renderer.destroy(true);
      // @ts-ignore
      this.renderer = undefined;
    }
    if (this.mesh) {
      this.mesh.destroy(true);
      // @ts-ignore
      this.mesh = undefined;
    }
    if (this.animationFrameReq > -1) {
      cancelAnimationFrame(this.animationFrameReq);
      this.animationFrameReq = -1;
    }
  };

  renderMesh = () => {
    if (!this.mesh) {
      return;
    }
    this.soundtrack.update();
    const spectrum = this.soundtrack.getSpectrum();
    const waveform = this.soundtrack.getWaveform();
    this.mesh.update(spectrum, waveform);
    this.renderer.render(this.stage);
    this.animationFrameReq = requestAnimationFrame(this.renderMesh);
  };

  render() {
    const { type, didSelect } = this.props;
    const { isOver } = this.state;
    return (
      <div
        onClick={() => didSelect(type)}
        onMouseOver={this.didEnter}
        onMouseOut={this.didLeave}
      >
        <div className="thumbs-grid-thumb">
          {isOver ? (
            <canvas ref={this.canvasEl} />
          ) : (
            <img src={`/media/sound-waves-thumbs/sound-wave-${type}.png`} />
          )}
        </div>
      </div>
    );
  }
}

interface Props {
  layer?: Layer;
}

export const SoundWaveSelector: React.FunctionComponent<Props> = props => {
  const didSelectType = async (type: SoundWaveType) => {
    trackEvent('Add Soundwave', { type });
    storeDispatch(new AddSoundWaveFrameCommand(props.layer?.id, type));
  };

  return (
    <Overlay>
      <ThumbsGrid title="Select sound wave type">
        {types.map(t => (
          <SoundwaveOption type={t} didSelect={didSelectType} key={t} />
        ))}
      </ThumbsGrid>
    </Overlay>
  );
};



// WEBPACK FOOTER //
// ./src/modals/SoundWaveSelector.tsx